import { makeAutoObservable, runInAction, computed } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { userService } from "../services";
import {
  createCreateForm,
  createEditForm,
  createListFilters,
  createListSort,
  createMeta,
  normalizePermissionOverrides,
  resolvePayload,
  resolveRelativeTime,
} from "./user-store.helpers";

const DEFAULT_PAGE_SIZE = 10;

const normalizeComparableString = (value) => String(value ?? "").trim();

const normalizeComparableId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return normalizeComparableId(value._id || value.id || value.key);
  }
  return String(value);
};

const normalizeComparableIdList = (value) => {
  const source = Array.isArray(value) ? value : value ? [value] : [];
  return source.map(normalizeComparableId).filter(Boolean).sort();
};

const normalizeComparablePermissions = (value) => {
  const source = Array.isArray(value) ? value : [];
  return source
    .map((item) =>
      String(
        typeof item === "object"
          ? item.key || item.id || item._id || item.name || ""
          : item,
      )
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean)
    .sort();
};

const normalizeComparablePermissionMode = (value) => {
  const mode = typeof value === "object" ? value?.mode : value;
  return String(mode || "")
    .trim()
    .toLowerCase() === "override"
    ? "override"
    : "inherit";
};

const normalizeComparablePermissionOverrides = (value) => {
  const overrides = normalizePermissionOverrides(value);
  const mode = normalizeComparablePermissionMode(overrides);
  if (mode !== "override") {
    return { mode: "inherit" };
  }

  return {
    mode,
    allow: normalizeComparablePermissions(overrides.allow),
    deny: normalizeComparablePermissions(overrides.deny),
  };
};

const normalizeEditFormForCompare = (form = createEditForm()) => {
  const role = normalizeComparableString(form.role);

  return {
    name: normalizeComparableString(form.name),
    username: normalizeComparableString(form.username),
    email: normalizeComparableString(form.email),
    role,
    customRoleName:
      role === "super_admin"
        ? normalizeComparableString(form.customRoleName)
        : "",
    status: normalizeComparableString(form.status),
    organizationUnitId: normalizeComparableIdList(form.organizationUnitId),
    assignedToParentCompany: Boolean(form.assignedToParentCompany),
    companyId: normalizeComparableString(form.companyId),
    permissionMode: normalizeComparablePermissionMode(form.permissionOverrides),
    permissionOverrides: normalizeComparablePermissionOverrides(
      form.permissionOverrides,
    ),
  };
};

const haveEditFormsChanged = (current, initial) =>
  JSON.stringify(normalizeEditFormForCompare(current)) !==
  JSON.stringify(normalizeEditFormForCompare(initial));

class UserStore {
  // List state
  users = [];
  usersTotal = 0;
  usersLoading = false;
  usersError = "";
  page = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  filters = createListFilters();
  sort = createListSort();

  // Detail state
  usersById = new Map();
  loadingById = new Map();
  savingById = new Map();
  errorById = new Map();
  editFormsById = new Map();
  initialEditFormsById = new Map();
  editMetaById = new Map();

  // Create form state
  createForm = createCreateForm();
  createLoading = false;
  createError = "";

  // Follow-up state
  followUpUserId = null;
  deleteLoading = false;
  unlockingUserId = null;

  // Can create user check
  canCreateUser = false;
  canCreateUserMessage = "";
  canCreateUserError = "";

  // Request management (simplified)
  _abortController = null;

  constructor() {
    makeAutoObservable(this, {
      hasUsers: computed,
      totalPages: computed,
      getUser: false,
      getEditForm: false,
      getEditMeta: false,
      hasEditChanges: false,
      hasPermissionModeChanged: false,
      isLoading: false,
      isSaving: false,
      getError: false,
      loadManagedUser: false,
      updateManagedUser: false,
      createManagedUser: false,
    });
  }

  // Computed properties
  get hasUsers() {
    return this.users.length > 0;
  }

  get totalPages() {
    return Math.ceil(this.usersTotal / this.pageSize);
  }

  // List management
  setFilters = (filters) => {
    this.filters = { ...this.filters, ...filters };
  };

  resetFilters = () => {
    this.filters = createListFilters();
  };

  setPage = (page) => {
    this.page = page;
  };

  setPageSize = (size) => {
    this.pageSize = size;
  };

  setSort = (sort) => {
    this.sort = { ...this.sort, ...sort };
  };

  listParams = () => ({
    username: this.filters.username,
    email: this.filters.email,
    company: this.filters.company,
    org: this.filters.org,
    role: this.filters.role,
    sortKey: this.sort.key,
    sortDirection: this.sort.direction,
    page: this.page,
    limit: this.pageSize,
    isExport: false,
  });

  mapUserFromApi = (item) => {
    const lastActiveAgo =
      item.lastActiveAgo ||
      resolveRelativeTime(
        item.lastActive || item.lastActiveAt || item.lastActiveOn,
      );

    return {
      id: item.id || item._id || "",
      userId: item.userId || item.id || item._id || "",
      createdBy: item.createdBy || "",
      displayName: item.displayName || item.name || "",
      username: item.username || item.name || "",
      email: item.email || "",
      role: item.role || "",
      customRoleName: item.customRoleName || "",
      company: item.company || "",
      organizationUnitId: item.organizationUnitId || "",
      assignedToParentCompany: item.assignedToParentCompany || false,
      organizationUnitName: item.organizationUnitName || "",
      onboardingPct: item.onboardingPct || 0,
      companyId:
        item.companyId ||
        item.company?.id ||
        item.company?._id ||
        item.company?.companyId ||
        item.companyDetails?.id ||
        item.companyDetails?._id ||
        item.companyDetails?.companyId ||
        "",
      incharge: item.incharge,
      projects: item.projects,
      onlineStatus: item.onlineStatus || "offline",
      lastActiveAgo,
      isNew: Boolean(item.isNew),
      randomPass: item.randomPass || null,
      lockUntil: item.lockUntil || null,
      failedLoginAttempts: Number(item.failedLoginAttempts) || 0,
      permissionOverrides: normalizePermissionOverrides(
        item.permissionOverrides,
      ),
      effectivePermissions: Array.isArray(item.effectivePermissions)
        ? item.effectivePermissions
        : Array.isArray(item.permissions)
          ? item.permissions
          : [],
      status: item.status || "",
    };
  };

  loadUsers = async () => {
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    runInAction(() => {
      this.usersLoading = true;
      this.usersError = "";
    });

    try {
      const params = this.listParams();
      const data = await userService.listUsers(params, {
        signal: this._abortController.signal,
        throwOnError: false,
      });

      if (!data) {
        runInAction(() => {
          this.users = [];
          this.usersTotal = 0;
          this.usersError = "Unable to load users.";
        });
        return;
      }

      const payload = resolvePayload(data) || {};
      const items = Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(payload?.data?.items)
          ? payload.data.items
          : [];
      const pagination = payload.pagination || payload?.data?.pagination || {};
      const mappedUsers = items.map((item) => this.mapUserFromApi(item));

      runInAction(() => {
        this.users = mappedUsers;
        this.usersTotal = Number.isFinite(pagination.total)
          ? pagination.total
          : mappedUsers.length;
        if (pagination.page && pagination.page !== this.page) {
          this.page = pagination.page;
        }
        if (pagination.limit && pagination.limit !== this.pageSize) {
          this.pageSize = pagination.limit;
        }
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        runInAction(() => {
          this.users = [];
          this.usersTotal = 0;
          this.usersError = "Unable to load users.";
        });
      }
    } finally {
      runInAction(() => {
        this.usersLoading = false;
      });
      this._abortController = null;
    }
  };

  updateOnlineUserStatus = (userId, status, lastActive) => {
    const normalizedId = userId ? String(userId) : "";
    if (!normalizedId) return;
    this.users = this.users.map((user) => {
      if (String(user.id) !== normalizedId) return user;

      return {
        ...user,
        onlineStatus: status,
        lastActiveAgo:
          status === "offline" && lastActive
            ? resolveRelativeTime(lastActive)
            : null,
      };
    });
  };

  checkCanCreateUser = async (companyId) => {
    if (!companyId) {
      return false;
    }
    try {
      const data = await userService.checkCanCreateUser(companyId, {
        throwOnError: false,
      });
      if (!data) {
        throw new Error("Error checking organization units and roles.");
      }

      const payload = resolvePayload(data) || {};
      runInAction(() => {
        this.canCreateUser = Boolean(payload.canCreate);
        this.canCreateUserMessage = payload.message || "";
      });

      return this.canCreateUser;
    } catch (error) {
      const message =
        error?.message || "Error checking organization units and roles";
      runInAction(() => {
        this.canCreateUser = false;
        this.canCreateUserError = message;
      });
      return false;
    }
  };

  // Detail management
  getUser = (userId) => this.usersById.get(userId) ?? null;

  getEditForm = (userId) => this.editFormsById.get(userId) ?? createEditForm();

  getEditMeta = (userId) => this.editMetaById.get(userId) ?? createMeta();

  hasEditChanges = (userId) => {
    if (!userId || !this.initialEditFormsById.has(userId)) return false;
    return haveEditFormsChanged(
      this.getEditForm(userId),
      this.initialEditFormsById.get(userId),
    );
  };

  hasPermissionModeChanged = (userId) => {
    if (!userId || !this.initialEditFormsById.has(userId)) return false;
    const currentMode = normalizeComparablePermissionMode(
      this.getEditForm(userId).permissionOverrides,
    );
    const initialMode = normalizeComparablePermissionMode(
      this.initialEditFormsById.get(userId)?.permissionOverrides,
    );
    return currentMode !== initialMode;
  };

  isLoading = (userId) => this.loadingById.get(userId) ?? false;

  isSaving = (userId) => this.savingById.get(userId) ?? false;

  getError = (userId) => this.errorById.get(userId) ?? "";

  setCreateField = (field, value) => {
    this.createForm = { ...this.createForm, [field]: value };
  };

  setCreatePermissionOverrides = (overrides) => {
    this.createForm = {
      ...this.createForm,
      permissionOverrides: normalizePermissionOverrides(overrides),
    };
  };

  resetCreateForm = () => {
    this.createForm = createCreateForm();
    this.createError = "";
  };

  updateEditField = (userId, field, value) => {
    if (!userId) return;
    const current = this.getEditForm(userId);
    this.editFormsById.set(userId, { ...current, [field]: value });
  };

  setEditPermissionOverrides = (userId, overrides) => {
    if (!userId) return;
    const current = this.getEditForm(userId);
    this.editFormsById.set(userId, {
      ...current,
      permissionOverrides: normalizePermissionOverrides(overrides),
    });
  };

  setEditFields = (userId, values = {}) => {
    if (!userId) return;
    const current = this.getEditForm(userId);
    this.editFormsById.set(userId, { ...current, ...values });
  };

  applyUserToForm = (userId, user) => {
    if (!userId || !user) return;

    const permissionOverrides = normalizePermissionOverrides(
      user.permissionOverrides,
    );

    //If inherit mode, populate allow from effectivePermissions
    if (permissionOverrides?.mode === "inherit") {
      permissionOverrides.allow = user.effectivePermissions || [];
      permissionOverrides.deny = [];
    }

    const nextForm = {
      name: user.displayName || user.name || "",
      username: user.username || "",
      email: user.email || "",
      role: user.role || "",
      customRoleName: user.customRoleName || "",
      status: user.status || "",
      // organizationUnitId: user.organizationUnitId || "",
      // organizationUnitName: user.organizationUnitName || "",
      organizationUnitId: Array.isArray(user.organizationUnitId)
        ? user.organizationUnitId.filter(Boolean).map((org) => org._id)
        : [],
      assignedToParentCompany: user.assignedToParentCompany || false,
      companyId:
        user.companyId ||
        user.company?.id ||
        user.company?._id ||
        user.company?.companyId ||
        user.companyDetails?.id ||
        user.companyDetails?._id ||
        user.companyDetails?.companyId ||
        "",
      permissionOverrides,
    };
    this.editFormsById.set(userId, nextForm);
    this.initialEditFormsById.set(userId, nextForm);
    this.editMetaById.set(userId, {
      company: user.company || "",
      createdAt: user.createdAt || null,
      updatedAt: user.updatedAt || null,
    });
  };

  invalidateUser = (userId) => {
    if (!userId) return;
    this.usersById.delete(userId);
    this.loadingById.delete(userId);
    this.savingById.delete(userId);
    this.errorById.delete(userId);
    this.editFormsById.delete(userId);
    this.initialEditFormsById.delete(userId);
    this.editMetaById.delete(userId);
  };

  clear = () => {
    this.usersById.clear();
    this.loadingById.clear();
    this.savingById.clear();
    this.errorById.clear();
    this.editFormsById.clear();
    this.initialEditFormsById.clear();
    this.editMetaById.clear();
  };

  loadManagedUser = async (userId, options = {}) => {
    if (!userId) return null;

    const { force = false } = options;
    const cached = this.usersById.get(userId);
    if (!force && cached) {
      this.applyUserToForm(userId, cached);
      return cached;
    }

    this.loadingById.set(userId, true);
    this.errorById.delete(userId);
    try {
      const data = await userService.getUser(userId, { throwOnError: false });
      if (!data) {
        throw new Error("Unable to load user details.");
      }

      const payload = resolvePayload(data);
      const user = payload?.data || payload || null;

      runInAction(() => {
        if (user) {
          this.usersById.set(userId, user);
          this.applyUserToForm(userId, user);
        } else {
          this.usersById.delete(userId);
          this.editFormsById.delete(userId);
          this.initialEditFormsById.delete(userId);
          this.editMetaById.delete(userId);
        }
        this.errorById.delete(userId);
      });

      return user;
    } catch (error) {
      const message = error?.message || "Unable to load user details.";
      runInAction(() => {
        this.usersById.delete(userId);
        this.editFormsById.delete(userId);
        this.initialEditFormsById.delete(userId);
        this.editMetaById.delete(userId);
        this.errorById.set(userId, message);
      });
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.loadingById.set(userId, false);
      });
    }
  };

  updateManagedUser = async (userId, payload, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) {
      throw new Error("User ID is required to update user details.");
    }
    this.savingById.set(userId, true);
    try {
      const actionPromise = userService.updateUser(userId, payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Saving user...",
          success: "User updated successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;

      const payloadData = resolvePayload(data);
      const user = payloadData?.data || payloadData || null;
      runInAction(() => {
        if (user) {
          this.usersById.set(userId, user);
          this.applyUserToForm(userId, user);
        }
        this.errorById.delete(userId);
      });
      return user;
    } catch (error) {
      const message = error?.message || "Unable to update user.";
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.savingById.set(userId, false);
      });
    }
  };

  createManagedUser = async (payload, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    this.createLoading = true;
    this.createError = "";
    try {
      const actionPromise = userService.createUser(payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Creating user...",
          success: "User created successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;

      const payloadData = resolvePayload(data);
      const created = payloadData?.data || payloadData || null;
      runInAction(() => {
        this.resetCreateForm();
      });
      return created;
    } catch (error) {
      const message = error?.message || "Unable to create user.";
      runInAction(() => {
        this.createError = message;
      });
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.createLoading = false;
      });
    }
  };

  assignFollowUp = async (userId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) return { success: false };
    runInAction(() => {
      this.followUpUserId = userId;
    });
    try {
      const actionPromise = userService.assignFollowUp(userId);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Updating follow-up...",
          success: "Follow-up updated.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }
      return await actionPromise;
    } finally {
      runInAction(() => {
        this.followUpUserId = null;
      });
    }
  };

  revokeFollowUp = async (userId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) return { success: false };
    runInAction(() => {
      this.followUpUserId = userId;
    });
    try {
      const actionPromise = userService.revokeFollowUp(userId, {
        showErrorToast: false,
      });
      if (shouldToast) {
        showToast.promise(
          actionPromise.then((result) => (result?.success ? true : false)),
          {
            loading: "Updating follow-up...",
            success: "Unfollow-up updated.",
            error: "Unable to remove follow-up.",
            ...toastMessages,
          },
        );
      }
      return await actionPromise;
    } finally {
      runInAction(() => {
        this.followUpUserId = null;
      });
    }
  };

  markUserDeleted = async (userId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) return { success: false };
    runInAction(() => {
      this.deleteLoading = true;
    });
    try {
      const actionPromise = userService.markUserDeleted(userId);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Deleting user...",
          success: "User deleted successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }
      return await actionPromise;
    } finally {
      runInAction(() => {
        this.deleteLoading = false;
      });
    }
  };

  unlockUser = async (userId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) return { success: false };

    runInAction(() => {
      this.unlockingUserId = userId;
    });

    try {
      const actionPromise = userService.unlockUser(userId);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Unlocking user...",
          success: "User unlocked successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;
      const payloadData = resolvePayload(data);
      const user = payloadData?.data || payloadData || null;

      runInAction(() => {
        if (user) {
          const mappedUser = this.mapUserFromApi(user);
          this.usersById.set(userId, user);
          this.applyUserToForm(userId, user);
          this.users = this.users.map((item) =>
            item.id === userId ? { ...item, ...mappedUser } : item,
          );
        }
        this.errorById.delete(userId);
      });

      return user;
    } catch (error) {
      const message = error?.message || "Unable to unlock user.";
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.unlockingUserId = null;
      });
    }
  };
  exportAllUsers = async () => {
    try {
      const baseParams = this.listParams();

      const initialData = await userService.listUsers(
        {
          ...baseParams,
          page: 1,
          limit: 1,
          isExport: true,
        },
        {
          throwOnError: true,
        },
      );

      if (!initialData) return [];

      const initialPayload = resolvePayload(initialData) || {};
      const pagination =
        initialPayload.pagination || initialPayload?.data?.pagination || {};

      const totalItems = pagination.total || 0;

      if (totalItems === 0) return [];

      const finalData = await userService.listUsers(
        {
          ...baseParams,
          page: 1,
          limit: totalItems, // <--- Sets the limit to the exact total records dynamically
          isExport: true,
        },
        {
          throwOnError: true,
        },
      );

      if (!finalData) return [];

      const finalPayload = resolvePayload(finalData) || {};
      const items = Array.isArray(finalPayload.items)
        ? finalPayload.items
        : Array.isArray(finalPayload?.data?.items)
          ? finalPayload.data.items
          : [];

      return items.map((item) => this.mapUserFromApi(item));
    } catch (error) {
      console.error("Failed to fetch all users for export:", error);
      throw error;
    }
  };
}
export const userStore = new UserStore();
